import React, { useContext, useEffect } from 'react';
import {
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography,
  Button,
} from '@material-ui/core';
import { DataGrid } from '@mui/x-data-grid';
import { useApi } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { notificationApiRef } from '../../../../apis/notificationApi';
import BackButton from '../../../utils/backButton';
import { promptApiRef } from '../../../../apis/promptManagementApi';
import { PromptContext } from '../providers/PromptProvider';
import { AuthContext } from '../../../../providers/AuthProvider';
import { HelpComponent } from '../../../utils/helpComponent';
import { useStyles } from '../styles';
import HELP_URL from '../../../utils/helpLinkConstant';
import SearchBar from '../common/SearchBar';
import { PromptPromotionDialog } from './promptPromotionDialog';

// const PromptPromotionDialog = React.lazy(() => import('./promptPromotionDialog'));

export const PromptPromotion = () => {
  const promptContext = useContext(PromptContext);
  const classes = useStyles();
  const authContext = useContext(AuthContext);
  const promptApi = useApi(promptApiRef);
  const { profEmail } = authContext;
  const [domainOptions, setDomainOptions] = React.useState<any[]>([]);
  const [personaOptions, setPersonaOptions] = React.useState<any[]>([]);
  const [subDomainOptions, setSubDomainOptions] = React.useState<any[]>([]);
  const [selectedDomain, setSelectedDomain] = React.useState<string>('');
  const [promptPromotionActivities, setPromptPromotionActivities] =
    React.useState<any[]>([]);
  const [selectedEnvironment, setSelectedEnvironment] =
    React.useState<string>('dev');
  const [selectedPersona, setSelectedPersona] = React.useState<string>('all');
  const [searchText, setSearchText] = React.useState<string>('');
  const [selectedSubDomain, setSelectedSubDomain] =
    React.useState<string>('all');
  const [rows, setRows] = React.useState<any[]>([]);
  const [filteredRows, setFilteredRows] = React.useState<any[]>([]);
  const [selectedPrompts, setSelectedPrompts] = React.useState<any[]>([]);
  const notificationApi = useApi(notificationApiRef);
  const navigate = useNavigate();
  const [dialogProps, setDialogProps] = React.useState<{
    open: boolean;
    title: string;
    onClose: () => void;
    onSubmit: (name: string, type: 'new' | 'update', id?: string) => void;
    workflows: any[];
  }>({
    open: false,
    title: '',
    onClose: () => {},
    onSubmit: () => {},
    workflows: [],
  });
  useEffect(() => {
    const filteredRows = rows.filter((row: any) => {
      const matchesDomain = !selectedDomain || row.domainId === selectedDomain;
      const matchesSubDomain =
        selectedSubDomain === 'all' || row.subDomainId === selectedSubDomain;
      const matchesPersona =
        selectedPersona === 'all' || row.personaId === selectedPersona;
      const matchesSearchText = row.promptKey
        .toLowerCase()
        .includes(searchText.toLowerCase());

      return (
        matchesDomain && matchesSubDomain && matchesPersona && matchesSearchText
      );
    });

    setFilteredRows(filteredRows);
  }, [rows, selectedDomain, selectedSubDomain, selectedPersona, searchText]);
  useEffect(() => {
    const fetchPromptPromotionActivities = async () => {
      try {
        const response = await promptApi.getPromptPublishQueue();
        if (response) {
          setPromptPromotionActivities(response);
        }
      } catch (error) {
        console.error('Error fetching prompt promotion activities:', error);
      }
    };

    fetchPromptPromotionActivities();
  }, []);
  const columns = [
    {
      field: 'promptKey',
      headerName: 'Prompt Key',
      editable: false,
      minWidth: 500,
      flex: 3,
    },
    {
      field: 'subDomain',
      headerName: 'Sub-Domain',
      editable: false,
      flex: 1,
    },
    {
      field: 'persona',
      headerName: 'Persona',
      editable: false,
      flex: 1,
    },
    {
      field: 'lastModified',
      headerName: 'Last Modified',
      editable: false,
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      editable: false,
      flex: 1,
    },
  ];

  useEffect(() => {
    if (!promptContext.domains && !promptContext.loading && profEmail) {
      promptContext.fetchData(true, profEmail);
    }
    if (promptContext.domains) {
      const domains = promptContext.domains.map((d: any) => ({
        label: d.name,
        value: d.id,
        id: d.id,
      }));
      setSelectedDomain(domains[0]?.value || '');
      setDomainOptions(domains);
    }
    if (promptContext.prompts) {
      const p = promptContext.prompts.flatMap((p: any) => {
        const domain = promptContext.domains.find(
          (d: any) => d.id === p.product,
        );
        if (!domain) return [];

        const subDomain = domain.subproducts.find(
          (sub: any) => sub.id === p.subproduct,
        );
        if (!subDomain) return [];

        const date = p.lastupdatedat || p.createdat;
        function createDateFromMMDDYYYY(dateString: string) {
          const parts = dateString.split('-'); // Split the string into month, day, and year
          const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed in JavaScript Date
          const day = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);

          return new Date(year, month, day);
        }
        const lastModifiedDate = new Date(
          createDateFromMMDDYYYY(date),
        ).toLocaleDateString();
        return p.persona.map((personaId: any) => {
          const persona = subDomain.personas.find(
            (per: any) => per.id === personaId,
          );
          return {
            id: p.id,
            persona: persona ? persona.name : 'Unknown',
            personaId: persona ? persona.id : 'Unknown',
            domain: domain.name,
            domainId: domain.id,
            subDomainId: subDomain.id,
            subDomain: subDomain.name,
            lastModified: lastModifiedDate,
            status: null,
            promptKey: p.promptkey,
          };
        });
      });
      setRows(p);
      const uniqueSubDomains = Array.from(
        new Map(
          p.map((item: any) => [
            item.subDomainId,
            { label: item.subDomain, value: item.subDomainId },
          ]),
        ).values(),
      );
      setSubDomainOptions([
        { label: 'All', value: 'all' },
        ...uniqueSubDomains,
      ]);

      const uniquePersonas = Array.from(
        new Map(
          p.map((item: any) => [
            item.personaId,
            { label: item.persona, value: item.personaId },
          ]),
        ).values(),
      );
      setPersonaOptions([{ label: 'All', value: 'all' }, ...uniquePersonas]);
    }
  }, [promptContext.domains]);
  const handleSubmit = async (
    id?: string,
  ) => {
    let response;

    try {
        const centralTime = new Date().toLocaleString("en-US", { timeZone: "America/Chicago" });
        const timestamp = centralTime.split(',')[0].replace(/[-/]/g, '');
        const domainName = domainOptions.find(d => d.value === selectedDomain)?.label || 'unknown';
        const name = `${domainName}-${selectedEnvironment}-${timestamp}`;

        response = await promptApi.promptPublishQueuePublish({
          destination: selectedEnvironment,
          prompt_ids: selectedPrompts.map((prompt: any) => prompt.id),
          domain_id: domainOptions.find(d => d.value === selectedDomain).id,
          name,
        });
      if (response) {
        console.log('Prompt publish request submitted successfully');
        notificationApi.sendNotification({
          message: `Prompt publish request submitted successfully`,
          severity: 'success',
          disapperAfterMs: 3000,
        });
        setDialogProps(v => ({ ...v, open: false }));
        setSelectedPrompts([]);
        navigate('/admin/manage-prompts/prompt-promotion/activity-log');
      }
    } catch(err) {
      console.log('Error in submitting prompt publish request', err);
      notificationApi.sendNotification({
        message: `Error in submitting prompt publish request`,
        severity: 'error',
        disapperAfterMs: 3000,
      });
    }
  };

  return (
    <Grid container direction="row">
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <BackButton />
        <HelpComponent helpUrl={HELP_URL.VIEW_PROMPTS} />
      </Grid>
      <Grid item xs={12}>
        <Paper
          className={`${classes.paper} ${classes.flex} ${classes.flexColumn}`}
        >
          <h2>Select Domain</h2>
          <div className={`${classes.flex} ${classes.alignCenter}`}>
            <Typography
              component="span"
              className={classes.promptPomotionFilterText}
            >
              Current domain
            </Typography>

            <FormControl variant="outlined" className={classes.formControl}>
              <Select
                value={selectedDomain}
                onChange={e => setSelectedDomain(e.target.value as string)}
              >
                {domainOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className={classes.dividerVertical} />
            <Typography
              component="span"
              className={classes.promptPomotionFilterText}
            >
              Publishing Environment
            </Typography>
            <FormControl variant="outlined" className={classes.formControl}>
              <Select
                value={selectedEnvironment}
                onChange={e => setSelectedEnvironment(e.target.value as string)}
              >
                <MenuItem value="dev">Development</MenuItem>
                <MenuItem value="uat">UAT</MenuItem>
                <MenuItem value="prod">Production</MenuItem>
              </Select>
            </FormControl>
          </div>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <div className={classes.promptPromotionTableHeading}>
            <h2>Publish prompts</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <SearchBar
                onTextChanged={(text: string) => {
                  setSearchText(text);
                }}
              />
              <FormControl
                variant="outlined"
                className={classes.tableFormControl}
              >
                <Select
                  fullWidth
                  value={selectedSubDomain}
                  onChange={e => setSelectedSubDomain(e.target.value as string)}
                >
                  {subDomainOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                className={classes.tableFormControl}
              >
                <Select
                  fullWidth
                  defaultValue={'all'}
                  value={selectedPersona}
                  onChange={e => setSelectedPersona(e.target.value as string)}
                >
                  {personaOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                className={classes.publishButton}
                onClick={handleSubmit}
                disabled={
                  selectedPrompts.length === 0 ||
                  !selectedEnvironment ||
                  !selectedDomain
                }
              >
                Publish
              </Button>
            </div>
          </div>
          <div
            style={{
              height: 600,
              width: '100%',
              padding: '0px 20px 20px 20px',
            }}
          >
            <DataGrid
              columns={columns}
              rows={filteredRows}
              pageSize={9}
              checkboxSelection
              onSelectionModelChange={(newSelection: any) => {
                if (newSelection) {
                  setSelectedPrompts(newSelection.map((id: any) => rows.find(row => row.id === id)));
                }
              }}
            />
          </div>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default PromptPromotion;
